/**
 * Launchpad IDs defined by the data team and extended with launchpads
 * supported by Codex for the Discovery page.
 */
export enum LaunchpadId {
  ARGUS = 'argus',
  ARROWFINANCE_IO = 'arrowfinance.io',
  BAGS = 'bags',
  BANKR = 'bankr',
  BASEAPP = 'baseapp',
  BELIEVE = 'believe',
  BOWFUN = 'bowfun',
  BUTTON = 'button',
  CIRCUS_TRADE = 'circus.trade',
  CLANKER = 'clanker',
  CLANKER_V4 = 'clanker_v4',
  DONTBLINK_FAMILY = 'dontblink.family',
  FAZE = 'faze',
  FEEL_CASH = 'feel.cash',
  FLAP = 'flap',
  FLAUNCH_GG = 'flaunch.gg',
  FOUR_MEME = 'four.meme',
  HOOD_FUN = 'hood.fun',
  JUPITER_STUDIO = 'jupiter_studio',
  KLIK = 'klik',
  LAUNCHHOOD = 'launchhood',
  LAUNCHLAB = 'launchlab',
  LEMON_FUN = 'lemon.fun',
  LETSCASH_FUN = 'letscash.fun',
  LIQUID = 'liquid',
  LITTLEJOHN_GG = 'littlejohn.gg',
  LONG = 'long.xyz',
  LUNCH_FUN = 'lunch.fun',
  METEORA_ALPHA_VAULT = 'meteora_alpha_vault',
  METEORA_DBC = 'meteoradbc',
  MOONSHOT = 'moonshot',
  NAD_FUN = 'nad.fun',
  NOXA = 'noxa',
  O1_EXCHANGE = 'o1.exchange',
  PEACH = 'peach',
  PONS = 'pons',
  POOLS_TRADE = 'pools.trade',
  PRINTR = 'printr',
  PUMP_FUN = 'pump.fun',
  STONKFUN = 'stonkfun',
  SUSHISWAP = 'sushiswap',
  TOLLY = 'tolly',
  TRENCH = 'trench',
  UNISWAP_AUCTIONS = 'uniswap_auctions',
  VARO = 'varo',
  VIRTUAL = 'virtual',
  ZORA = 'zora',
  ZORA_CREATOR = 'zora_creator',
}

export type LaunchpadDefinition = {
  /** Name of the launchpad */
  name: string;
  /** Logo of the launchpad */
  logo: string;
  /** Codex ID of the launchpad (see https://docs.codex.io/launchpads) */
  codexId?: string;
  /** Whether Token API is expected to provide launchpad attribution. */
  tokenApiSupport?: boolean;
};

export const launchpadDefinitions: Record<LaunchpadId, LaunchpadDefinition> = {
  [LaunchpadId.ARGUS]: {
    name: 'Argus',
    logo: '/images/launchpads/argus.webp',
    codexId: 'Argus',
  },
  [LaunchpadId.ARROWFINANCE_IO]: {
    name: 'Arrow Finance',
    logo: '/images/launchpads/arrow-finance.webp',
    tokenApiSupport: true,
  },
  [LaunchpadId.BAGS]: {
    name: 'Bags',
    logo: '/images/launchpads/bags.webp',
    codexId: 'BAGS',
    tokenApiSupport: true,
  },
  [LaunchpadId.BANKR]: { name: 'Bankr', logo: '/images/launchpads/bankr.webp', codexId: 'Bankr' },
  [LaunchpadId.BASEAPP]: {
    name: 'Base App',
    logo: '/images/launchpads/base.webp',
    codexId: 'Baseapp',
  },
  [LaunchpadId.BELIEVE]: {
    name: 'Believe',
    logo: '/images/launchpads/believe.webp',
    codexId: 'Believe',
  },
  [LaunchpadId.BOWFUN]: {
    name: 'Bow.fun',
    logo: '/images/launchpads/bow-fun.webp',
    codexId: 'bow.fun',
    tokenApiSupport: true,
  },
  [LaunchpadId.BUTTON]: {
    name: 'Button.fun',
    logo: '/images/launchpads/button.webp',
    tokenApiSupport: true,
  },
  [LaunchpadId.CIRCUS_TRADE]: {
    name: 'Circus',
    logo: '/images/launchpads/circus.webp',
    tokenApiSupport: true,
  },
  [LaunchpadId.CLANKER]: {
    name: 'Clanker',
    logo: '/images/launchpads/clanker.webp',
    codexId: 'Clanker',
  },
  [LaunchpadId.CLANKER_V4]: {
    name: 'Clanker V4',
    logo: '/images/launchpads/clanker.webp',
    codexId: 'Clanker V4',
  },
  [LaunchpadId.DONTBLINK_FAMILY]: {
    name: 'dontblink',
    logo: '/images/launchpads/dontblink.webp',
    tokenApiSupport: true,
  },
  [LaunchpadId.FAZE]: {
    name: 'Faze',
    logo: '/images/launchpads/faze.webp',
    codexId: 'Faze',
  },
  [LaunchpadId.FEEL_CASH]: {
    name: 'Feel.cash',
    logo: '/images/launchpads/feel-cash.webp',
    codexId: 'Feel.cash',
  },
  [LaunchpadId.FLAP]: {
    name: 'Flap',
    logo: '/images/launchpads/flap.webp',
    codexId: 'Flap',
    tokenApiSupport: true,
  },
  [LaunchpadId.FLAUNCH_GG]: {
    name: 'Flaunch.gg',
    logo: '/images/launchpads/flaunch.webp',
    tokenApiSupport: true,
    codexId: 'Flaunch',
  },
  [LaunchpadId.FOUR_MEME]: {
    name: 'Four.meme',
    logo: '/images/launchpads/four-meme.webp',
    codexId: 'Four.meme',
    tokenApiSupport: true,
  },
  [LaunchpadId.HOOD_FUN]: {
    name: 'Hood.fun',
    logo: '/images/launchpads/hood-fun.webp',
    codexId: 'hood.fun',
    tokenApiSupport: true,
  },
  [LaunchpadId.JUPITER_STUDIO]: {
    name: 'Jupiter Studio',
    logo: '/images/launchpads/jupiter-studio.webp',
    codexId: 'Jupiter Studio',
  },
  [LaunchpadId.KLIK]: {
    name: 'Klik',
    logo: '/images/launchpads/klik.webp',
    tokenApiSupport: true,
  },
  [LaunchpadId.LAUNCHHOOD]: {
    name: 'LaunchHood',
    logo: '/images/launchpads/launchhood.webp',
    tokenApiSupport: true,
  },
  [LaunchpadId.LAUNCHLAB]: {
    name: 'LaunchLab',
    logo: '/images/launchpads/launchlab.webp',
    codexId: 'LaunchLab',
  },
  [LaunchpadId.LEMON_FUN]: {
    name: 'Lemon.fun',
    logo: '/images/launchpads/lemon-fun.webp',
    tokenApiSupport: true,
  },
  [LaunchpadId.LETSCASH_FUN]: {
    name: 'Letscash.fun',
    logo: '/images/launchpads/letscash-fun.webp',
    tokenApiSupport: true,
  },
  [LaunchpadId.LIQUID]: {
    name: 'Liquid',
    logo: '/images/launchpads/liquid.webp',
    codexId: 'Liquid',
  },
  [LaunchpadId.LITTLEJOHN_GG]: {
    name: 'LittleJohn',
    logo: '/images/launchpads/littlejohn.webp',
    tokenApiSupport: true,
  },
  [LaunchpadId.LONG]: {
    name: 'LONG',
    logo: '/images/launchpads/long.webp',
    codexId: 'LONG',
    tokenApiSupport: true,
  },
  [LaunchpadId.LUNCH_FUN]: {
    name: 'Lunch.fun',
    logo: '/images/launchpads/lunch-fun.webp',
    tokenApiSupport: true,
  },
  [LaunchpadId.METEORA_ALPHA_VAULT]: {
    name: 'Meteora Alpha Vault',
    logo: '/images/launchpads/meteora.webp',
    codexId: 'Meteora Alpha Vault',
  },
  [LaunchpadId.METEORA_DBC]: {
    name: 'MeteoraDBC',
    logo: '/images/launchpads/meteora.webp',
    codexId: 'MeteoraDBC',
  },
  [LaunchpadId.MOONSHOT]: {
    name: 'Moonshot',
    logo: '/images/launchpads/moonshot.webp',
    codexId: 'Moonshot',
  },
  [LaunchpadId.NAD_FUN]: {
    name: 'Nad.Fun',
    logo: '/images/launchpads/nad-fun.webp',
    codexId: 'Nad.Fun',
  },
  [LaunchpadId.NOXA]: {
    name: 'Noxa',
    logo: '/images/launchpads/noxa.webp',
    codexId: 'NOXA Fun',
    tokenApiSupport: true,
  },
  [LaunchpadId.O1_EXCHANGE]: {
    name: 'o1.exchange',
    logo: '/images/launchpads/o1-exchange.webp',
    codexId: 'o1.exchange',
    tokenApiSupport: true,
  },
  [LaunchpadId.PEACH]: {
    name: 'Peach',
    logo: '/images/launchpads/peach.webp',
    codexId: 'Peach',
  },
  [LaunchpadId.PONS]: {
    name: 'Pons',
    logo: '/images/launchpads/pons.webp',
    codexId: 'pons',
    tokenApiSupport: true,
  },
  [LaunchpadId.POOLS_TRADE]: {
    name: 'Pools.trade',
    logo: '/images/launchpads/pools-trade.webp',
  },
  [LaunchpadId.PRINTR]: {
    name: 'Printr',
    logo: '/images/launchpads/printr.webp',
    codexId: 'Printr',
  },
  [LaunchpadId.PUMP_FUN]: {
    name: 'Pump.fun',
    logo: '/images/launchpads/pump-fun.webp',
    codexId: 'Pump.fun',
    tokenApiSupport: false,
  },
  [LaunchpadId.STONKFUN]: {
    name: 'StonkFun',
    logo: '/images/launchpads/stonk-fun.webp',
    codexId: 'StonkFun',
  },
  [LaunchpadId.SUSHISWAP]: {
    name: 'Sushiswap',
    logo: '/images/launchpads/sushi-launch.webp',
    codexId: 'Sushi Launch',
  },
  [LaunchpadId.TOLLY]: {
    name: 'Tolly',
    logo: '/images/launchpads/tolly.webp',
    codexId: 'Tolly',
  },
  [LaunchpadId.TRENCH]: {
    name: 'Trench',
    logo: '/images/launchpads/trench.webp',
    tokenApiSupport: true,
    codexId: 'Trench',
  },
  [LaunchpadId.UNISWAP_AUCTIONS]: {
    name: 'Uniswap Auctions',
    logo: '/images/launchpads/uniswap-cca.webp',
    codexId: 'UniswapCCA',
    tokenApiSupport: true,
  },
  [LaunchpadId.VARO]: {
    name: 'Varo',
    logo: '/images/launchpads/varo.webp',
    tokenApiSupport: true,
  },
  [LaunchpadId.VIRTUAL]: {
    name: 'Virtuals',
    logo: '/images/launchpads/virtuals.webp',
    codexId: 'Virtuals',
    tokenApiSupport: true,
  },
  [LaunchpadId.ZORA]: { name: 'Zora Posts', logo: '/images/tokens/zora.webp', codexId: 'Zora' },
  [LaunchpadId.ZORA_CREATOR]: {
    name: 'Zora Creator Coins',
    logo: '/images/tokens/zora.webp',
    codexId: 'Zora Creator',
  },
};

export const isLaunchpadSupported = (id?: string | null): id is LaunchpadId =>
  id != null && (Object.values(LaunchpadId) as string[]).includes(id);
